import logger from '@/lib/logger';
import type { AiAnalysisState, SuggestedAction } from './aiAnalysisTypes';

// Simple hash function to generate IDs from strings
export function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < Math.min(str.length, 100); i++) { // Only hash first 100 chars
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

export function dedupeById<T extends { id?: string }>(items: T[], context: string): T[] {
  const seen = new Set<string>();
  const duplicates: string[] = [];
  const result: T[] = [];

  items.forEach(item => {
    const rawId = item?.id;
    const normalizedId = typeof rawId === 'string'
      ? rawId.trim()
      : rawId
      ? String(rawId)
      : '';

    if (!normalizedId) {
      result.push(item);
      return;
    }

    if (seen.has(normalizedId)) {
      duplicates.push(normalizedId);
      return;
    }

    seen.add(normalizedId);
    result.push(item);
  });

  if (duplicates.length > 0) {
    logger.warn('aiAnalysisSlice', 'Filtered duplicate suggested action IDs', {
      context,
      duplicates
    });
  }

  return result;
}

// Constants for stable reordering
export const REORDER_DEBOUNCE_MS = 5000;  // 5 seconds - minimum time between reorders (officers focused on caller)
const REORDER_THRESHOLD = 10;      // Priority difference needed to trigger reorder
const MAX_DISPLAY_ACTIONS = 15;    // Max actions to display (legacy, now more flexible)
const DISPLAY_CAP_BASE_COUNT = 10; // Baseline size for cap ratios
const DISPLAY_CAP_ENFORCED_COUNT = MAX_DISPLAY_ACTIONS; // Apply caps across visible items

// ============================================================================
// NEW BACKLOG-BASED VISIBLE LIMITS
// ============================================================================
// Questions: capped in visible pool; overflow stays ready in queue
// Per-group visibility: each type is capped INDEPENDENTLY so one group can never
// hide another (a burst of suggestions must not crowd out tasks). Best-by-
// priority within each group. Conflicts/Deceptions: UNLIMITED, display immediately.
const MAX_VISIBLE_QUESTIONS = 8;
const MAX_VISIBLE_TASKS = 4;
const MAX_VISIBLE_SUGGESTIONS = 4;

// Legacy caps (kept for backward compatibility but overridden by new logic)
const DISPLAY_TYPE_CAPS_BASE: Record<string, number> = {
  task: 2,      // Now part of combined 4 limit with suggestions
  suggestion: 2, // Now part of combined 4 limit with tasks
};

// Type balance penalties - prevents any single type from dominating the list
// Lower penalty = more slots allowed for that type
const TYPE_BALANCE_PENALTIES: Record<string, number[]> = {
  question: [0, 0, 2, 4, 6, 10, 15, 20, 30, 50],  // questions can dominate more (up to 6-7)
  task: [0, 5, 15, 30, 50],                        // tasks limited to ~3
  suggestion: [0, 10, 30, 50],                     // suggestions limited to ~2
};

const DEFAULT_PRIORITY = 50;
const MAX_PRIORITY = 100;
const DEPRIORITIZE_TARGET_INDEX = 9; // 10th item (0-based)

/**
 * Parse priority from any value, ensuring it's always a valid number.
 * Handles strings from backend that may not be properly typed.
 */
export function parsePriority(priority: unknown): number {
  if (typeof priority === 'number' && !isNaN(priority)) {
    return Math.min(MAX_PRIORITY, Math.max(1, priority));
  }
  if (typeof priority === 'string') {
    const parsed = parseInt(priority, 10);
    if (!isNaN(parsed)) {
      return Math.min(MAX_PRIORITY, Math.max(1, parsed));
    }
  }
  return DEFAULT_PRIORITY;
}

export function clampPriority(priority?: number): number {
  const value = typeof priority === 'number' ? priority : DEFAULT_PRIORITY;
  return Math.min(MAX_PRIORITY, Math.max(1, value));
}

export function getDeprioritizedPriority(actions: SuggestedAction[], currentPriority: number): number {
  if (actions.length === 0) {
    return Math.min(MAX_PRIORITY, currentPriority + 1);
  }

  const sorted = [...actions].sort((a, b) =>
    clampPriority(a.priority) - clampPriority(b.priority)
  );
  const referenceIndex = Math.min(DEPRIORITIZE_TARGET_INDEX, sorted.length - 1);
  const referencePriority = clampPriority(sorted[referenceIndex]?.priority);
  const candidate = Math.min(MAX_PRIORITY, referencePriority + 1);
  return Math.min(MAX_PRIORITY, Math.max(currentPriority, candidate));
}

/**
 * Calculate adjusted priority with type balancing
 * Penalizes actions when too many of same type are already in display
 */
export function getAdjustedPriority(action: SuggestedAction, currentDisplay: SuggestedAction[]): number {
  const basePriority = action.priority ?? DEFAULT_PRIORITY;
  const typeCount = currentDisplay.filter(a => a.type === action.type).length;
  const penalties = TYPE_BALANCE_PENALTIES[action.type] ?? [50];
  const penalty = penalties[Math.min(typeCount, penalties.length - 1)] ?? 50;
  return basePriority + penalty;
}

/**
 * Split actions into visible and queued (backlog) based on new visibility rules:
 * - Questions: first runway visible, overflow stays ready in queue
 * - Tasks + Suggestions: Combined max 4, best by priority
 * - Conflicts/Deceptions: UNLIMITED (all go to visible immediately)
 */
export function splitActionsByDisplayCaps(
  actions: SuggestedAction[],
  opts: { preserveOrder?: boolean } = {},
): {
  visible: SuggestedAction[];
  queued: SuggestedAction[];
} {
  const visible: SuggestedAction[] = [];
  const queued: SuggestedAction[] = [];
  let questionCount = 0;
  let taskCount = 0;
  let suggestionCount = 0;

  // Normally pick best-by-priority. When preserveOrder is set (officer-speaking
  // freeze), keep the caller's order so the cap selects the already-frozen set
  // instead of re-deriving a fresh priority pick that could swap which items show.
  const sorted = opts.preserveOrder
    ? actions
    : [...actions].sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));

  for (const action of sorted) {
    const actionType = action.type ?? 'suggestion';
    const displayState =
      ((action as SuggestedAction & { display_state?: string; displayState?: string }).display_state ||
        (action as SuggestedAction & { display_state?: string; displayState?: string }).displayState ||
        '').toLowerCase();
    const isConflictOrDeception = actionType === 'conflicting_statement' || actionType === 'deception_detection';

    if (displayState === 'hidden' || displayState === 'archived') {
      continue;
    }

    // Conflicts/Deceptions: always visible, no limit
    if (isConflictOrDeception) {
      visible.push(action);
      continue;
    }

    // Questions: keep the first runway visible; queue the rest.
    if (actionType === 'question') {
      if (questionCount < MAX_VISIBLE_QUESTIONS) {
        visible.push(action);
        questionCount++;
      } else {
        queued.push(action);
      }
      continue;
    }

    // Tasks and Suggestions are capped INDEPENDENTLY (per-group visibility) so a
    // burst of one never hides the other. Best-by-priority within each group.
    if (actionType === 'task') {
      if (taskCount < MAX_VISIBLE_TASKS) {
        visible.push(action);
        taskCount++;
      } else {
        queued.push(action);
      }
      continue;
    }
    if (actionType === 'suggestion') {
      if (suggestionCount < MAX_VISIBLE_SUGGESTIONS) {
        visible.push(action);
        suggestionCount++;
      } else {
        queued.push(action);
      }
      continue;
    }

    // Unknown types go to visible by default
    visible.push(action);
  }

  return { visible, queued };
}

/**
 * Get display order - sorts by priority (lower = higher priority)
 * Ensures consistent ordering regardless of how actions were added
 * Returns array of action IDs in display order (max 15)
 */
export function calculateBalancedOrder(actions: SuggestedAction[]): string[] {
  if (actions.length === 0) return [];

  // Sort by priority (lower = higher priority) then take top 15
  const sorted = [...actions].sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50));
  const { visible } = splitActionsByDisplayCaps(sorted);
  return visible.map(a => a.id);
}

/**
 * Check if order changed significantly enough to warrant a reorder
 * Returns true if the new order is meaningfully different
 */
export function orderChangedSignificantly(oldOrder: string[], newOrder: string[]): boolean {
  if (oldOrder.length !== newOrder.length) return true;

  // Check if any items moved more than 2 positions or new items appeared
  for (let i = 0; i < newOrder.length; i++) {
    const oldIdx = oldOrder.indexOf(newOrder[i]);
    if (oldIdx === -1) return true; // New item appeared
    if (Math.abs(oldIdx - i) > 2) return true; // Item moved significantly
  }

  return false;
}

export function updatePriorityChangeTimesForOrder(
  state: AiAnalysisState,
  oldOrder: string[],
  newOrder: string[],
  now: number
) {
  const oldIndexById = new Map<string, number>();
  oldOrder.forEach((id, idx) => {
    oldIndexById.set(id, idx);
  });

  newOrder.forEach((id, newIndex) => {
    const oldIndex = oldIndexById.get(id);
    if (oldIndex === undefined || oldIndex !== newIndex) {
      state.lastPriorityChangeTimes[id] = now;
    }
  });
}

// ---------------------------------------------------------------------------
// Activity feed (timeline) merge — a feed may never SHRINK to a strict subset.
//
// 2026-08-17 (/test-call extreme, roha-biki-jire-8066): the live Timeline
// showed 9 events; the caller hung up, the room was re-created and the NEW
// agent-backend job analysed only the transcript it had seen since re-join,
// pushing "1 activity events". Both payload reducers replaced the feed
// wholesale (guarded only against EMPTY), so the archive — and the incident
// report — carried 1 event. Policy: an incoming feed at least as long as the
// current one is an authoritative regeneration and replaces it (that is how
// corrections and re-orderings arrive); a SHORTER incoming feed is treated as
// partial and is UNIONED into the existing one (existing order kept, genuinely
// new events appended). Identity is the event text — string events by value,
// object events by `shortSummary` (the officer's `note` never counts).
// ---------------------------------------------------------------------------
type FeedEvent = string | { shortSummary?: string; note?: string };

export function activityEventKey(event: FeedEvent): string {
  if (typeof event === 'string') return `s:${event.trim()}`;
  return `o:${String(event?.shortSummary ?? '').trim()}`;
}

export function mergeActivityFeedEvents<T extends FeedEvent>(existing: T[], incoming: T[]): T[] {
  if (incoming.length === 0) return existing;
  if (incoming.length >= existing.length) return incoming;
  const seen = new Set(existing.map(activityEventKey));
  const appended = incoming.filter((e) => !seen.has(activityEventKey(e)));
  return appended.length === 0 ? existing : [...existing, ...appended];
}

